import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Link,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  Link as LinkIcon,
  Download as DownloadIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { QUESTIONNAIRE, AGENCIES, PHASES, STATE_SPECIFIC_INFO } from '../../data/identityRecoveryData';

function IdentityRecoveryGuide() {
  const [selectedSituations, setSelectedSituations] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [completedSteps, setCompletedSteps] = useState([]);
  const [expandedPhase, setExpandedPhase] = useState('foundation');

  // Handle situation selection
  const handleSituationChange = (value) => {
    setSelectedSituations((prev) => {
      if (value === 'all') {
        return prev.includes('all') ? [] : ['all'];
      }
      const newSelected = prev.includes(value)
        ? prev.filter((v) => v !== value && v !== 'all')
        : [...prev.filter((v) => v !== 'all'), value];
      return newSelected;
    });
  };

  // Handle completed steps
  const handleStepToggle = (stepId) => {
    setCompletedSteps((prev) =>
      prev.includes(stepId) ? prev.filter((id) => id !== stepId) : [...prev, stepId]
    );
  };

  // Get relevant agencies based on selections
  const getRelevantAgencies = () => {
    if (selectedSituations.length === 0) {
      return AGENCIES;
    }

    const situationQuestion = QUESTIONNAIRE.find((q) => q.id === 'situation');
    const selectedOptions = situationQuestion.options.filter((opt) =>
      selectedSituations.includes(opt.value)
    );

    const relevantCategories = new Set();
    selectedOptions.forEach((opt) => {
      opt.categories.forEach((cat) => relevantCategories.add(cat));
    });

    return AGENCIES.filter(
      (agency) =>
        agency.required || relevantCategories.has(agency.category)
    );
  };

  // Group agencies by phase
  const getAgenciesByPhase = (phase) => {
    return getRelevantAgencies().filter((agency) => agency.phase === phase);
  };

  // Download checklist functionality
  const downloadChecklist = () => {
    const agencies = getRelevantAgencies();
    let checklistText = 'Identity Recovery Checklist\n';
    checklistText += '='.repeat(50) + '\n\n';

    Object.values(PHASES).forEach((phase) => {
      const phaseAgencies = agencies.filter((a) => a.phase === phase);
      if (phaseAgencies.length > 0) {
        checklistText += `\n${phase.toUpperCase().replace('_', ' ')}\n`;
        checklistText += '-'.repeat(50) + '\n';
        phaseAgencies.forEach((agency) => {
          checklistText += `\n☐ ${agency.name}\n`;
          checklistText += `   ${agency.description}\n`;
          if (agency.phone) checklistText += `   Phone: ${agency.phone}\n`;
          if (agency.url) checklistText += `   URL: ${agency.url}\n`;
          checklistText += `   Estimated Time: ${agency.estimatedTime}\n`;
        });
      }
    });

    const blob = new Blob([checklistText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'identity-recovery-checklist.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const stateQuestion = QUESTIONNAIRE.find((q) => q.id === 'state');
  const situationQuestion = QUESTIONNAIRE.find((q) => q.id === 'situation');
  const reportsQuestion = QUESTIONNAIRE.find((q) => q.id === 'reports_filed');
  const stateInfo = selectedState && STATE_SPECIFIC_INFO[selectedState];

  return (
    <Box>
      {/* Questionnaire Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon color="primary" />
          Personalized Recovery Plan
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Answer these questions to create a customized identity recovery checklist.
        </Typography>

        {/* Situation Question */}
        <Box sx={{ mb: 3 }}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
              {situationQuestion.question}
            </FormLabel>
            <FormGroup>
              {situationQuestion.options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={selectedSituations.includes(option.value)}
                      onChange={() => handleSituationChange(option.value)}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Box>

        {/* State Selection */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1, fontWeight: 600 }}>{stateQuestion.question}</FormLabel>
            <Select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select your state</em>
              </MenuItem>
              {stateQuestion.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                  {option.hasEnhancedRights && (
                    <Chip label={option.law} size="small" color="primary" sx={{ ml: 1 }} />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* State-Specific Rights */}
        {stateInfo && (
          <Alert severity="info" icon={<SecurityIcon />} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>{stateInfo.name}: {stateInfo.law}</strong>
            </Typography>
            <Typography variant="body2">{stateInfo.description}</Typography>
            <List dense>
              {stateInfo.rights.map((right, idx) => (
                <ListItem key={idx} sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <CheckCircleIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={right} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {/* Foundation Steps Checklist */}
        <Box sx={{ mb: 2 }}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
              {reportsQuestion.question}
            </FormLabel>
            <FormGroup>
              {reportsQuestion.options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={completedSteps.includes(option.value)}
                      onChange={() => handleStepToggle(option.value)}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Box>

        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={downloadChecklist}
          fullWidth
          sx={{ mt: 2 }}
        >
          Download Your Personalized Checklist
        </Button>
      </Paper>

      {/* Agencies by Phase */}
      {Object.entries({
        [PHASES.FOUNDATION]: 'Foundation: Essential First Steps',
        [PHASES.HIDDEN_BUREAUS]: 'Hidden Bureaus: Beyond the Big Three',
        [PHASES.STATE_RIGHTS]: 'State-Specific Rights & Resources',
      }).map(([phase, title]) => {
        const agencies = getAgenciesByPhase(phase);
        if (agencies.length === 0) return null;

        return (
          <Accordion
            key={phase}
            expanded={expandedPhase === phase}
            onChange={() => setExpandedPhase(expandedPhase === phase ? '' : phase)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="h6">{title}</Typography>
                <Chip label={`${agencies.length} agencies`} size="small" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {agencies.map((agency) => (
                  <Card key={agency.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {agency.name}
                            {agency.required && (
                              <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {agency.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            <Chip label={agency.actionType} size="small" />
                            <Chip label={agency.estimatedTime} size="small" variant="outlined" />
                            {agency.category && (
                              <Chip
                                label={agency.category.replace('_', ' ').toUpperCase()}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Contact Information */}
                      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {agency.url && (
                          <Button
                            component={Link}
                            href={agency.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<LinkIcon />}
                            size="small"
                            variant="outlined"
                          >
                            Visit Website
                          </Button>
                        )}
                        {agency.phone && (
                          <Button
                            component={Link}
                            href={`tel:${agency.phone}`}
                            startIcon={<PhoneIcon />}
                            size="small"
                            variant="outlined"
                          >
                            {agency.phone}
                          </Button>
                        )}
                      </Box>

                      {/* Instructions */}
                      {agency.instructions && agency.instructions.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                            Step-by-Step Instructions:
                          </Typography>
                          <List dense>
                            {agency.instructions.map((instruction, idx) => (
                              <ListItem key={idx} sx={{ pl: 0 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                    {idx + 1}.
                                  </Typography>
                                </ListItemIcon>
                                <ListItemText primary={instruction} />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Warning for incomplete foundation */}
      {completedSteps.length < 4 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>Complete Foundation Steps First</strong>
          </Typography>
          <Typography variant="body2">
            It's critical to complete all foundation steps before moving to specialized bureaus. The FTC
            report and police report are required by most agencies to process your identity theft claims.
          </Typography>
        </Alert>
      )}

      {/* Privacy Notice */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>Privacy & Data Protection</strong>
        </Typography>
        <Typography variant="body2">
          Your selections are only stored locally in your browser and are never sent to our servers. All
          personalization happens on your device to protect your privacy.
        </Typography>
      </Alert>
    </Box>
  );
}

export default IdentityRecoveryGuide;
